// Toasts (#1201), after the Tailwind Plus "simple" notification, in the live region at the end
// of index.html. The message is plain text, set as textContent (#1427): toasts quote lead emails
// and error messages, which must never become markup. "processing" toasts stay until
// dismissToast is called with the element it returned, every other type closes after 4.5 s.
//
// The region is a manual popover (#1447): a modal <dialog> such as the lead drawer sits in the
// browser's top layer, above every z-index, so a toast drawn in the page was invisible while one
// was open — a failed email send looked like nothing had happened. A popover shown later stacks
// above the dialog; it is shown with the first toast and hidden with the last.
import { icon, solidIcon } from "./icons.js";

function regionHost(region) {
  return (typeof region.closest === "function" && region.closest("[popover]")) || document.getElementById("toastRegion");
}

function raise(region) {
  const host = regionHost(region);
  if (!host || typeof host.showPopover !== "function") return;
  try {
    if (!host.matches(":popover-open")) host.showPopover();
  } catch (_) {
    // Not connected, or popovers unsupported: the region still renders in the page as before.
  }
}

function lower(region) {
  const host = regionHost(region);
  if (!host || typeof host.hidePopover !== "function" || region.childElementCount > 0) return;
  try {
    if (host.matches(":popover-open")) host.hidePopover();
  } catch (_) {}
}

const ICONS = {
  success: icon("check-circle", "size-6 text-green-500 dark:text-green-400"),
  error: icon("x-circle", "size-6 text-red-500 dark:text-red-400"),
  warning: icon("exclamation-triangle", "size-6 text-amber-500 dark:text-amber-400"),
  info: icon("information-circle", "size-6 text-sky-500 dark:text-sky-400"),
  processing: `<span class="block size-5 animate-spin rounded-full border-2 border-gray-200 border-t-paprika-600 dark:border-white/15 dark:border-t-paprika-400" role="status"></span>`
};

function variant(type) {
  if (type === "danger" || type === "error") return "error";
  if (type === "success" || type === "warning" || type === "processing") return type;
  return "info";
}

export function showToast(message, type = "info") {
  const region = document.getElementById("toastContainer");
  if (!region) return null;

  const kind = variant(type);
  const toast = document.createElement("div");
  toast.dataset.toast = kind;
  toast.className =
    "pointer-events-auto w-full max-w-sm rounded-lg bg-white shadow-lg outline-1 outline-black/5 transition duration-300 ease-out " +
    "starting:translate-y-2 starting:opacity-0 sm:starting:translate-x-2 sm:starting:translate-y-0 " +
    "dark:bg-gray-800 dark:-outline-offset-1 dark:outline-white/10";
  toast.innerHTML = `
    <div class="flex items-start gap-x-3 p-4">
      <div class="shrink-0">${ICONS[kind]}</div>
      <p data-toast-message class="w-0 flex-1 pt-0.5 text-sm font-medium text-gray-900 dark:text-white"></p>
      <button type="button" data-toast-close class="inline-flex shrink-0 rounded-md text-gray-400 hover:text-gray-500 focus:outline-2 focus:outline-offset-2 focus:outline-paprika-600 dark:hover:text-white dark:focus:outline-paprika-500">
        <span class="sr-only">Close</span>${solidIcon("x-mark", "size-5")}
      </button>
    </div>`;
  toast.querySelector("[data-toast-message]").textContent = String(message ?? "");
  toast.querySelector("[data-toast-close]").addEventListener("click", () => dismissToast(toast));
  region.appendChild(toast);
  raise(region);

  if (kind !== "processing") {
    setTimeout(() => dismissToast(toast), 4500);
  }
  return toast;
}

export function dismissToast(toastEl) {
  if (toastEl && toastEl.parentNode) {
    const region = toastEl.parentNode;
    toastEl.remove();
    lower(region);
  }
}
